import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import useEscapeLayer from "./useEscape";
import UnsentGuard from "./record/UnsentGuard";
// The composer wears the record's grammar wherever it opens (Today's rows,
// Follow-ups, the calendar), so it brings the stylesheet with it rather than
// relying on the record having been loaded first.
import "./record/record.css";
import {
  composerTitle,
  draftNoun,
  generatorLabel,
  replyQuote,
  offeredChips,
  chipsZone,
  slotInBody,
  insertAt,
  beforeSignOff,
  timeSentence,
  wantsBookingLink,
  bookingUrlOf,
  withBookingLink,
  bookingChip,
  ccAddress,
  ccName,
  confirmWords,
  errorWords,
  firstWord,
  FRESH_KINDS,
  unsent,
  addFiles,
  fileSize,
} from "./record/composerModel.mjs";

/**
 * THE COMPOSER. Where an email is written before anything leaves.
 *
 * It began as a display: the server generated the text, wrote it straight into
 * the Gmail thread as a draft, and this panel showed what had already happened.
 * That worked while the words were fixed. They are not fixed: the model writes
 * a starting point and Gabriel finishes it, which is the entire reason the
 * generated-versus-sent pair is worth keeping at all, so the text has to be
 * editable HERE, before it is filed anywhere.
 *
 * Which means generation and filing are two steps now. Opening the panel writes
 * nothing to Gmail; "Save to Gmail" sends up whatever is in these two fields.
 * The old order put the model's version in his mailbox every time he opened the
 * panel to look at a draft and closed it again.
 *
 * Filing into an existing thread needs gmail.compose. When the connected token
 * predates that scope the panel says so beside the button and offers the thread
 * plus a copy button, rather than silently starting a NEW message beside the
 * conversation it is answering, which was the original bug here.
 *
 * SEND IS TWO CLICKS, and it is the only control in the CRM that cannot be taken
 * back. Everything else here is a status a person can change again; a sent email
 * is in somebody else's inbox. The second click names the recipient ("Send this
 * to Ella?"), because the mistake worth preventing is not "he did not mean to
 * send" so much as "he did not mean to send it to THEM": the panel is opened from
 * a queue where the rows look alike. Send and its confirm are black: the yellow
 * was spent on the button that opened the composer (the house rule, one yellow
 * per screen).
 *
 * THE MOCKUP'S GRAMMAR (design/lead-record, frames 1d, 1h, 1i): one line saying
 * what it answers ("Replying to Ella, Sep 30: …") with how the draft was made at
 * its right, then To / Cc / Subject as a small form, the body, the offered times
 * as chips in the lead's zone, and the actions under a rule. On the phone it is
 * a full-screen sheet: Cancel · title · Send on top, their message quoted, the
 * Gmail links in a bar at the bottom.
 *
 * AN EDITED DRAFT IS NEVER THROWN AWAY WITHOUT ASKING (reality.txt [15]). Esc,
 * the ×, the phone's Cancel and the panel around it closing all go through one
 * question, "Throw away your reply to Ella?" (record/UnsentGuard.js): Discard,
 * Keep editing, or Save to Gmail when the draft can be filed. A draft nobody
 * has touched closes on Esc like any other layer: it is the model's words, and
 * opening the composer again writes them again.
 *
 * Props (all but `action` optional, so every older caller keeps working):
 *   action       { kind, contact_id, age_days, activity_id, event_id, verb,
 *                  contact_email, contact_name?, booking_link? }, what to draft
 *   onClose      () => void, closes it (after the guard, when edited)
 *   onSent       (res) => void, the page behind reacts to a send: Today turns the
 *                row into a receipt, the record reloads and leaves one in Next.
 *                The panel behaves the same with or without it.
 *   contactName  "Ella Moss": the To line, the confirm and the guard (else
 *                action.contact_name, else the address)
 *   quote        { line, head, text } from composerModel.replyQuote, or null for
 *                none; left undefined, it is read off `timeline` when given
 *   timeline     the company timeline's rows (the record's), for the quote
 *   people       [{ email, first_name, last_name }], names for the Cc boxes
 *   zones        the record's { theirs, mine }: theirs null means no offered times
 *   booking      GET /booking/contact/:id's card when the host already has it
 *   bookingLink  true puts the booking link in the starting draft (Re-engage);
 *                a first email always gets it
 *   fullScreen   the phone's full-screen sheet instead of the docked card
 */
export default function ReplyPanel({
  action,
  onClose,
  onSent,
  contactName = null,
  quote = undefined,
  timeline = null,
  people = null,
  zones = undefined,
  booking = null,
  bookingLink = undefined,
  fullScreen = false,
}) {
  const [state, setState] = useState({ loading: true });
  // What the model wrote, kept whole. The fields below drift away from it as
  // soon as he types, and the difference is the point. When the composer put the
  // booking link in, this holds the text WITH it: "edited" is about his edits.
  const generated = useRef(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  // Errors, each shown beside the control that failed (the brief: "Errors show
  // next to the control that failed"), never in a list at the bottom.
  const [sendError, setSendError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [copyError, setCopyError] = useState(null);
  const [ccError, setCcError] = useState(null);
  // Once it has gone the panel is the receipt. The page behind reloads on
  // `onSent`, which can hand this panel a new kind or quiet-days count (the
  // thread now ends with our message), and that must not draft a second email.
  const sentRef = useRef(false);
  // Who else gets it, picked from the call's other attendees; and whether it
  // starts a new thread under its own subject instead of answering the old one.
  const [cc, setCc] = useState([]);
  const [newThread, setNewThread] = useState(false);
  const [card, setCard] = useState(null);
  // Files on the email: { id, name, size, type, file }, the File kept as it is and
  // read into base64 only when it is filed or sent. Refusals show beside the link.
  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [dropping, setDropping] = useState(false);
  const fileInputRef = useRef(null);
  // The unsent-draft guard, open: { proceed } is what happens after Discard (or
  // after Save to Gmail has filed it): the composer's own close, or the close of
  // the panel around it that asked through the registry.
  const [guard, setGuard] = useState(null);
  const [guardBusy, setGuardBusy] = useState(false);
  const [guardError, setGuardError] = useState(null);

  const rootRef = useRef(null);
  const bodyRef = useRef(null);
  const ccInputRef = useRef(null);
  // Where he last left the caret in the body, so an offered time lands there;
  // null until he has been in the field (a fresh textarea reports 0, the top).
  const caretRef = useRef(null);
  const pendingCaret = useRef(null);

  const name = contactName || action.contact_name || null;
  const wants = wantsBookingLink(action.kind, bookingLink === true || action.booking_link === true ? true : undefined);
  // Read inside the draft effect, never a dependency of it: the record reloads
  // after every write and hands a new booking object each time, and that must not
  // ask the model for a second draft.
  const bookingRef = useRef(booking);
  bookingRef.current = booking;

  useEffect(() => {
    if (sentRef.current) return undefined;
    let live = true;
    const cardAsk = !wants
      ? Promise.resolve(null)
      : bookingRef.current
        ? Promise.resolve(bookingRef.current)
        : api(`/booking/contact/${action.contact_id}`).catch(() => null);
    Promise.all([
      api("/actions/reply", {
        method: "POST",
        body: JSON.stringify({
          contact_id: action.contact_id,
          kind: action.kind,
          quiet_days: action.age_days,
          // Set when it was opened from a call on the timeline: the draft is then
          // written from THAT call's notes rather than the most recent one.
          activity_id: action.activity_id || null,
          // Set when it was opened from the calendar's "They didn't show": the
          // draft is the no-show rescue for THAT call.
          event_id: action.event_id || null,
        }),
      }),
      cardAsk,
    ])
      .then(([data, bookingCard]) => {
        if (!live) return;
        // The booking link goes into the STARTING text, before he has typed a
        // word, so it is part of what he reads and edits rather than something
        // added behind his back at send time. The corpus row the server wrote
        // keeps the model's own words; the link is his sentence, not the model's.
        const url = bookingUrlOf(bookingCard);
        const start = url ? withBookingLink(data.draft.body || "", url) : data.draft.body || "";
        generated.current = { subject: data.draft.subject || "", body: start };
        setSubject(data.draft.subject || "");
        setBody(start);
        // The welcome email opens with Lukasz already on it, as a new thread.
        setCc(data.cc_default || []);
        setNewThread(!!data.new_thread_default);
        setCard(wants ? bookingCard || { url: null } : null);
        setSaved(null);
        setConfirming(false);
        setSendError(null);
        setSaveError(null);
        setState({ loading: false, data });
      })
      .catch((err) => live && setState({ loading: false, error: err.message }));
    return () => {
      live = false;
    };
  }, [action.contact_id, action.kind, action.age_days, action.activity_id, action.event_id, wants]);

  const { loading, data, error } = state;
  const g = generated.current;
  // The words, for "how it was made"; the files too, for asking before closing:
  // a draft with the deck on it is not the model's draft any more.
  const wordsEdited = !!g && !sent && (subject !== g.subject || body !== g.body);
  const edited = wordsEdited || (!!g && !sent && files.length > 0);
  const isSent = !!sent;

  const title = composerTitle(action.kind, name);
  const shown = useMemo(() => {
    if (quote !== undefined) return quote;
    if (!timeline) return null;
    return replyQuote({ rows: timeline, contactId: action.contact_id, kind: action.kind, name, zones });
  }, [quote, timeline, action.contact_id, action.kind, name, zones]);

  /* ------------------------------------------------------------ closing, asked */

  // Every way out of an edited draft comes here: the ×, Cancel, Esc, and the
  // panel around it (through the registry below). Untouched, it just closes.
  const requestClose = (proceed = onClose) => {
    const go = () => proceed && proceed();
    if (edited) {
      setGuardError(null);
      setGuard({ proceed: go });
    } else go();
  };

  // While it holds edits, the panel around it can ask before closing it
  // (composerModel's unsent registry): the shell calls unsent.ask(onClose, root)
  // and, when this composer is inside that root, the guard opens here.
  const askRef = useRef(null);
  askRef.current = (proceed) => {
    setGuardError(null);
    setGuard({ proceed });
  };
  useEffect(() => {
    if (!edited || isSent) return undefined;
    return unsent.hold(rootRef.current, (proceed) => askRef.current && askRef.current(proceed));
  }, [edited, isSent]);

  // Esc, one layer at a time: it takes back the "Send this to …?" confirm first;
  // then an edited draft asks before it goes (the guard is its own layer, so Esc
  // on the guard means Keep editing); an untouched one closes.
  const escape = () => {
    if (sending) return;
    // Called only from events, after render, so keepEditing (below) is defined.
    if (guard) keepEditing();
    else if (confirming) setConfirming(false);
    else requestClose();
  };
  useEscapeLayer(true, () => {
    if (guard) return;
    escape();
  });
  // ESC FROM INSIDE THE SUBJECT OR THE BODY, which is where the cursor is while he
  // writes. The app's layers never take a key typed into a field (escape.mjs, so a
  // panel's Esc cannot throw away a draft from the textarea), which left Esc in the
  // composer doing nothing at all; frame 1i's guard is "Esc or × on an edited
  // composer". The fields hand it to the composer's own rule instead: the confirm
  // first, then the guard when edited, else close. escape.mjs stays as it is.
  const fieldEscape = (e) => {
    if (e.key !== "Escape" || e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey) return;
    e.preventDefault();
    escape();
  };

  /* ------------------------------------------------------------ editing */

  // Any keystroke invalidates the draft sitting in Gmail: it holds the words as
  // they were when it was filed, and saying otherwise is how he sends the
  // version he had already rejected.
  // A keystroke also takes back the confirm: the words being sent are not the
  // words that were confirmed.
  const invalidate = () => {
    setSaved(null);
    setCopied(false);
    setConfirming(false);
    setSendError(null);
    setSaveError(null);
    setCopyError(null);
  };
  const edit = (setter) => (e) => {
    setter(e.target.value);
    invalidate();
  };
  const noteCaret = (e) => {
    caretRef.current = [e.target.selectionStart, e.target.selectionEnd];
  };

  // Words put in by a chip (an offered time, the booking link): the same as typing
  // them, so they invalidate a filed draft and count as an edit.
  const putBody = (next, caret = null) => {
    setBody(next);
    invalidate();
    pendingCaret.current = caret;
  };
  useEffect(() => {
    const el = bodyRef.current;
    const at = pendingCaret.current;
    if (!el || at === null) return;
    pendingCaret.current = null;
    el.focus();
    el.setSelectionRange(at, at);
    caretRef.current = [at, at];
  }, [body]);

  const addTime = (chip) => {
    if (caretRef.current) {
      const [s, e] = caretRef.current;
      const out = insertAt(body, chip.phrase, s, e);
      putBody(out.body, out.caret);
    } else {
      putBody(beforeSignOff(body, timeSentence(chip)));
    }
  };
  const addBookingLink = () => {
    const url = bookingUrlOf(card);
    if (url) putBody(withBookingLink(body, url));
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(body);
      setCopied(true);
      setCopyError(null);
    } catch {
      setCopied(false);
      setCopyError(errorWords(null, "copy"));
    }
  };

  // Changing who gets it, or where it goes, takes back a confirm and a saved
  // draft the same way a keystroke does.
  // Anyone the checkboxes do not offer: another campaign manager, a second person
  // on their side. Checked on the server too; a typo is refused, never dropped.
  const [extraCc, setExtraCc] = useState("");
  const [ccOpen, setCcOpen] = useState(false);
  // True when nothing is left in the field to fix (added, or empty), so the
  // field can fold back into "Cc someone else".
  const addCc = () => {
    const { email, error: bad } = ccAddress(extraCc);
    if (bad) {
      setCcError(bad);
      return false;
    }
    setCcError(null);
    if (!email) return true;
    setCc((list) => (list.includes(email) ? list : [...list, email]));
    setExtraCc("");
    invalidate();
    return true;
  };
  useEffect(() => {
    if (ccOpen && ccInputRef.current) ccInputRef.current.focus();
  }, [ccOpen]);

  // Files from the picker or dropped on the composer. Adding or taking one off is
  // a change like a keystroke: a filed draft no longer matches, the confirm goes.
  const attach = (list) => {
    const incoming = Array.from(list || []).map((file) => ({
      id: `${file.name}:${file.size}:${file.lastModified}`,
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    }));
    if (!incoming.length) return;
    const next = addFiles(files, incoming);
    setFileError(next.error);
    if (next.files.length !== files.length) {
      setFiles(next.files);
      invalidate();
    }
  };
  const unattach = (id) => {
    setFiles((list) => list.filter((f) => f.id !== id));
    setFileError(null);
    invalidate();
  };
  const dropHandlers = isSent
    ? {}
    : {
        onDragOver: (e) => {
          if (!Array.from(e.dataTransfer.types || []).includes("Files")) return;
          e.preventDefault();
          setDropping(true);
        },
        onDragLeave: (e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false);
        },
        onDrop: (e) => {
          if (!e.dataTransfer.files || !e.dataTransfer.files.length) return;
          e.preventDefault();
          setDropping(false);
          attach(e.dataTransfer.files);
        },
      };

  const toggleCc = (email) => {
    setCc((list) => (list.includes(email) ? list.filter((e) => e !== email) : [...list, email]));
    invalidate();
  };

  // The subject follows the switch only while he has not rewritten it: a
  // subject he typed is his, whichever way the switch goes.
  const toggleNewThread = () => {
    const next = !newThread;
    const fresh = data && data.new_thread_subject;
    const original = g && g.subject;
    if (next && fresh && subject === original) setSubject(fresh);
    if (!next && fresh && subject === fresh && original) setSubject(original);
    setNewThread(next);
    invalidate();
  };

  /* ------------------------------------------------------------ filing and sending */

  // With no stored thread the email is a new one whatever the switch says, so it
  // is FILED as one: a first email can be saved to Gmail too (frame 1h), where
  // asking the server for a draft inside a thread that does not exist is refused.
  const asNew = newThread || !(data && data.thread_id);

  const file = async () => {
    const attachments = await encodeFiles(files);
    const res = await api("/actions/reply/draft", {
      method: "POST",
      // draft_id ties this back to the row the server opened when it wrote the
      // draft, so what he settled on is stored against what we produced.
      body: JSON.stringify({ contact_id: action.contact_id, draft_id: data.draft_id, subject, body, cc, new_thread: asNew, attachments }),
    });
    setSaved(res);
    return res;
  };

  const saveToGmail = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await file();
      if (res.needs_scope) setSaveError(errorWords("permission", "save"));
      else if (!res.created) setSaveError(errorWords(res.error, "save"));
    } catch (err) {
      setSaved(null);
      setSaveError(errorWords(err.message, "save"));
    } finally {
      setSaving(false);
    }
  };

  const send = async () => {
    setSending(true);
    setSendError(null);
    try {
      const attachments = await encodeFiles(files);
      const res = await api("/actions/reply/send", {
        method: "POST",
        // His switch only: with no thread the server sends a fresh message anyway,
        // and a thread that appeared since the draft (they wrote) is answered in.
        body: JSON.stringify({ contact_id: action.contact_id, draft_id: data.draft_id, subject, body, cc, new_thread: newThread, attachments }),
      });
      sentRef.current = true;
      setSent(res);
      setConfirming(false);
      if (onSent) onSent(res);
    } catch (err) {
      setSendError(errorWords(err.message, "send"));
      setConfirming(false);
    } finally {
      setSending(false);
    }
  };

  /* ------------------------------------------------------------ the guard's answers */

  const discard = () => {
    const proceed = guard && guard.proceed;
    setGuard(null);
    if (proceed) proceed();
  };
  const keepEditing = () => {
    setGuard(null);
    setGuardError(null);
  };
  // Save to Gmail from the guard: filed, then closed. A save that fails keeps the
  // guard open with the reason beside the button; the words stay where they are.
  const saveAndClose = async () => {
    setGuardBusy(true);
    setGuardError(null);
    try {
      const res = await file();
      if (res.created) {
        const proceed = guard && guard.proceed;
        setGuard(null);
        if (proceed) proceed();
      } else {
        setGuardError(errorWords(res.needs_scope ? "permission" : res.error, "save").text);
      }
    } catch (err) {
      setGuardError(errorWords(err.message, "save").text);
    } finally {
      setGuardBusy(false);
    }
  };

  /* ------------------------------------------------------------ what is shown */

  const offered = (data && data.cc_options) || [];
  // Typed addresses stay listed as ticked boxes, so one can be taken off again.
  const ccOptions = [...offered, ...cc.filter((e) => !offered.includes(e))];
  const chips = data ? offeredChips(data.offered_slots, zones) : [];
  const zone = chipsZone(chips);
  const linkChip = wants && card ? bookingChip(card, body) : null;
  const to = (data && data.to) || action.contact_email || null;
  const toName = name || to;
  const sure = confirmWords({ name, to, cc, people: people || [], newThread: asNew && !!(data && data.thread_id), hasThread: !!(data && data.thread_id), subject, files });
  const gen = data ? generatorLabel(data.draft, wordsEdited) : null;
  const canSave = !!(data && data.can_draft);
  // Said beside Save to Gmail before he presses it, as the old panel did: a token
  // that predates gmail.compose cannot file into the thread.
  const composeMissing = data && !data.can_draft && data.thread_id && !newThread && !saveError && !(saved && saved.created);

  const guardEl = guard && (
    <Layer>
      <div className="rec-dialog__backdrop" onClick={keepEditing}>
        <div className="rec-dialog" onClick={(e) => e.stopPropagation()}>
          <UnsentGuard
            name={firstWord(name) || "them"}
            what={draftNoun(action.kind)}
            canSave={canSave}
            busy={guardBusy}
            error={guardError}
            onDiscard={discard}
            onKeep={keepEditing}
            onSave={saveAndClose}
          />
        </div>
      </div>
    </Layer>
  );

  const confirmCard = (
    <div className="rec-confirm rec-cmp__confirm" role="alertdialog" aria-label={sure.title}>
      {/* The recipient is named here and nowhere else in the flow, because this
          is the last moment it can matter. */}
      <div className="rec-confirm__title">{sure.title}</div>
      <div className="rec-body">{sure.detail}</div>
      <div className="rec-confirm__actions">
        <button type="button" className="rec-btn rec-btn--plain" disabled={sending} onClick={() => setConfirming(false)}>
          Cancel
        </button>
        <button type="button" className="rec-btn rec-btn--dark" onClick={send} disabled={sending}>
          {sending ? "Sending…" : "Yes, send it"}
        </button>
      </div>
    </div>
  );

  const fields = data && (
    <div className="rec-cmp__fields">
      <span className="rec-cmp__k">To</span>
      <span className="rec-cmp__to" title={to || undefined}>
        <b>{toName}</b>
        {/* On the phone how it was made rides on the To line (frame 1h); the
            docked card says it at the right of the quote line instead. */}
        {fullScreen && gen && <span className="rec-meta"> · {gen}</span>}
      </span>
      <span className="rec-cmp__k">Cc</span>
      <span className="rec-cmp__cc">
        {ccOptions.map((email) => (
          <label key={email} className="rec-cmp__check" title={email}>
            <input
              type="checkbox"
              className="rec-cmp__box"
              checked={cc.includes(email)}
              onChange={() => toggleCc(email)}
              disabled={isSent}
            />
            {ccName(email, people || [])}
          </label>
        ))}
        {!isSent &&
          (ccOpen ? (
            <span className="rec-cmp__ccadd">
              <input
                ref={ccInputRef}
                type="email"
                className="rec-cmp__ccinput"
                value={extraCc}
                onChange={(e) => {
                  setExtraCc(e.target.value);
                  setCcError(null);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (addCc()) setCcOpen(false);
                  }
                }}
                onBlur={() => {
                  if (addCc()) setCcOpen(false);
                }}
                placeholder="name@company.com"
                aria-label="Add a Cc address"
              />
              {ccError && <span className="rec-err">{ccError}</span>}
            </span>
          ) : (
            <button type="button" className="rec-link rec-cmp__ccsome" onClick={() => setCcOpen(true)}>
              Cc someone else
            </button>
          ))}
      </span>
      <span className="rec-cmp__k">Subject</span>
      <span className="rec-cmp__subjectrow">
        <input
          className="rec-cmp__subject"
          value={subject}
          onChange={edit(setSubject)}
          onKeyDown={fieldEscape}
          placeholder="Subject"
          aria-label="Subject"
          readOnly={isSent}
        />
        {data.thread_id && !isSent && (
          <label className="rec-cmp__check rec-cmp__check--sub">
            <input type="checkbox" className="rec-cmp__box" checked={newThread} onChange={toggleNewThread} />
            Send as a new email
          </label>
        )}
      </span>
    </div>
  );

  // What the draft stands on, said before he sends it: the rescue that found no
  // times, the call it was written from (and the pricing ladder the server
  // printed into it), and an email with no thread to answer.
  const notes = data && (
    <>
      {data.warning && <p className="rec-cmp__note rec-err">{data.warning}</p>}
      {data.call && (
        <p className="rec-cmp__note">
          From the call{data.call.title ? ` "${data.call.title}"` : ""}
          {data.call.promised && data.call.promised.length ? ` · you said you'd: ${data.call.promised.join("; ").toLowerCase()}` : ""}
          {data.call.pricing
            ? ` · pricing: the ${data.call.pricing.label || (data.call.pricing.ladder === "larger" ? "$5K" : "$3.5K")} ladder, because ${data.call.pricing.reason}`
            : ""}
        </p>
      )}
      {!data.thread_id && !FRESH_KINDS.has(action.kind) && (
        <p className="rec-cmp__note">
          No stored email thread for this contact, so there's nothing to reply inside. This goes as a new email.
        </p>
      )}
    </>
  );

  const bodyEl = data && (
    <>
      <textarea
        ref={bodyRef}
        className="rec-cmp__body"
        value={body}
        onChange={(e) => {
          edit(setBody)(e);
          noteCaret(e);
        }}
        onSelect={noteCaret}
        onClick={noteCaret}
        onKeyUp={noteCaret}
        onKeyDown={fieldEscape}
        rows={Math.min(20, Math.max(8, body.split("\n").length + 2))}
        aria-label="Email body"
        readOnly={isSent}
      />
      {/* The signature is letterhead, not prose, so it is not in the textarea:
          one stray keystroke in a table of contact details is worse than not
          editing it per message, and keeping it out leaves the generated / sent
          pairs in the corpus as pure prose. Saying so is what keeps that honest
          rather than hidden. */}
      {!isSent && <p className="rec-cmp__sig">Your Gmail signature is added when this sends.</p>}
    </>
  );

  // The files, under the words the way Gmail puts them: each a chip with its size
  // and an ×, then "Attach files". Dropping files anywhere on the composer works
  // too. Once sent, the chips stay as the record of what went, without the ×.
  const filesEl = data && (files.length > 0 || !isSent) && (
    <div className="rec-cmp__files">
      {files.map((f) => (
        <span key={f.id} className="rec-cmp__chip rec-cmp__file" title={f.name}>
          <span className="rec-cmp__filename">{f.name}</span>
          <span className="rec-meta">{fileSize(f.size)}</span>
          {!isSent && (
            <button type="button" className="rec-cmp__unattach" aria-label={`Remove ${f.name}`} onClick={() => unattach(f.id)}>
              ×
            </button>
          )}
        </span>
      ))}
      {!isSent && (
        <>
          <button type="button" className="rec-link rec-cmp__attach" onClick={() => fileInputRef.current && fileInputRef.current.click()}>
            {files.length ? "Attach more" : "Attach files"}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              attach(e.target.files);
              // The same file can be picked again after it was taken off.
              e.target.value = "";
            }}
          />
          {fileError && <span className="rec-err">{fileError}</span>}
        </>
      )}
    </div>
  );

  // The offered times, as chips in THEIR zone (frame 1d: "Offer times in ET:").
  // Tinted while the body names that time; an untinted one puts it in, at the
  // caret, or as its own sentence above the sign-off. A first touch's times are
  // real free slots from his scheduler resolved into the lead's zone; showing them
  // is how he can tell the email quotes his calendar rather than the model
  // inventing a Tuesday. None when we do not know their zone.
  const chipsEl = data && (chips.length > 0 || linkChip) && (
    <div className="rec-cmp__chips">
      {!fullScreen && chips.length > 0 && <span className="rec-meta">Offer times in {zone || "their zone"}:</span>}
      {chips.map((chip) => {
        const inBody = slotInBody(chip, body);
        return inBody || isSent ? (
          <span key={chip.text} className={`rec-cmp__chip${inBody ? " is-in" : ""}`} title="In the email">
            {chip.text}
          </span>
        ) : (
          <button key={chip.text} type="button" className="rec-cmp__chip" title="Put this time in the email" onClick={() => addTime(chip)}>
            {chip.text}
          </button>
        );
      })}
      {linkChip &&
        (linkChip.state === "out" && !isSent ? (
          <button type="button" className="rec-cmp__chip" onClick={addBookingLink}>
            {linkChip.text}
          </button>
        ) : linkChip.state === "in" ? (
          <span className="rec-cmp__chip">{linkChip.text}</span>
        ) : (
          <span className="rec-meta">{linkChip.text}</span>
        ))}
    </div>
  );

  // Gmail's own permission states, said where the control would be.
  const sendMissing = data && !data.can_send && !isSent && (
    <span className="rec-cmp__why">
      Sending needs the gmail.send permission. <SettingsLink />.
    </span>
  );

  const saveBtn = data && !isSent && (
    <>
      <button
        type="button"
        className={fullScreen ? "rec-link rec-cmp__barlink" : "rec-btn rec-btn--plain"}
        onClick={saveToGmail}
        disabled={saving || !body.trim()}
      >
        {saving ? "Saving…" : saved && saved.created ? "Saved" : "Save to Gmail"}
      </button>
      {saveError && <ErrorText err={saveError} />}
      {composeMissing && (
        <span className="rec-cmp__why">
          Gmail can't file drafts into the thread yet. <SettingsLink />.
        </span>
      )}
    </>
  );

  const links = data && (
    <>
      {saved && saved.created && saved.draft_url && !isSent && (
        <a className="rec-cmp__a" href={saved.draft_url} target="_blank" rel="noreferrer">
          Open draft in Gmail ↗
        </a>
      )}
      {isSent
        ? sent.thread_url && (
            <a className="rec-cmp__a" href={sent.thread_url} target="_blank" rel="noreferrer">
              Open the thread ↗
            </a>
          )
        : data.thread_url && !newThread && (
            <a className="rec-cmp__a" href={data.thread_url} target="_blank" rel="noreferrer">
              Open the thread ↗
            </a>
          )}
      {!isSent && (
        <>
          <button type="button" className="rec-link rec-cmp__a" onClick={copy}>
            {copied ? "Copied" : "Copy text"}
          </button>
          {copyError && <ErrorText err={copyError} />}
        </>
      )}
    </>
  );

  const sentNotes = isSent && !sent.logged && (
    <p className="rec-cmp__note">
      The email went out, but it could not be written onto the timeline. It will appear there when Gmail next syncs.
    </p>
  );

  /* ------------------------------------------------------------ the phone: full screen */

  if (fullScreen) {
    return (
      <div
        ref={rootRef}
        className={`rec rec-cmp rec-cmp--full${dropping ? " is-dropping" : ""}`}
        role="dialog"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        {...dropHandlers}
      >
        <div className="rec-cmp__top">
          <button type="button" className="rec-cmp__cancel" onClick={() => requestClose()}>
            {isSent ? "Done" : "Cancel"}
          </button>
          <span className="rec-cmp__title">{title}</span>
          {data && data.can_send && !isSent ? (
            <button
              type="button"
              className="rec-btn rec-btn--dark rec-cmp__topsend"
              onClick={() => setConfirming(true)}
              disabled={!body.trim() || !subject.trim() || sending}
            >
              Send
            </button>
          ) : (
            <span className="rec-cmp__topgap" />
          )}
        </div>
        {sendError && (
          <div className="rec-cmp__toperr">
            <ErrorText err={sendError} />
          </div>
        )}
        <div className="rec-cmp__scroll">
          {/* Their last message, quoted at the top (the brief's phone composer). */}
          {shown && (
            <div className="rec-cmp__quotebox">
              <div className="rec-cmp__quotehead">{shown.head}</div>
              {shown.text}
            </div>
          )}
          {loading && <p className="rec-cmp__note">Drafting…</p>}
          {error && <p className="rec-cmp__note"><ErrorText err={errorWords(error, "draft")} /></p>}
          {fields}
          {notes}
          {bodyEl}
          {filesEl}
          {chipsEl}
          {sentNotes}
          {isSent && <p className="rec-cmp__note">Sent to {toName}.</p>}
          {sendMissing && <p className="rec-cmp__note">{sendMissing}</p>}
        </div>
        {data && (
          <div className="rec-cmp__bar">
            {saveBtn}
            {links}
          </div>
        )}
        {confirming && (
          <Layer>
            <div className="rec-dialog__backdrop" onClick={() => !sending && setConfirming(false)}>
              <div className="rec-dialog" onClick={(e) => e.stopPropagation()}>
                {confirmCard}
              </div>
            </div>
          </Layer>
        )}
        {guardEl}
      </div>
    );
  }

  /* ------------------------------------------------------------ docked */

  return (
    <div
      ref={rootRef}
      className={`rec rec-cmp${dropping ? " is-dropping" : ""}`}
      onClick={(e) => e.stopPropagation()}
      {...dropHandlers}
    >
      <div className="rec-cmp__head">
        {/* What it answers, in one line; a welcome or a first email that answers
            nothing says what it is instead. */}
        <span className="rec-cmp__quote" title={shown && shown.text ? shown.text : undefined}>
          {shown ? shown.line : title}
        </span>
        {gen && <span className="rec-cmp__gen">{gen}</span>}
        <button type="button" className="rec-x" aria-label="Close the composer" title={edited ? "Close (asks first)" : "Close (Esc)"} onClick={() => requestClose()}>
          ×
        </button>
      </div>
      {loading && <p className="rec-cmp__note rec-cmp__note--pad">Drafting…</p>}
      {error && (
        <p className="rec-cmp__note rec-cmp__note--pad">
          <ErrorText err={errorWords(error, "draft")} />
        </p>
      )}
      {data && (
        <>
          {fields}
          {notes}
          {bodyEl}
          {filesEl}
          {chipsEl}
          {confirming ? (
            <div className="rec-cmp__acts rec-cmp__acts--confirm">{confirmCard}</div>
          ) : isSent ? (
            <div className="rec-cmp__acts">
              {/* Once it has gone, the fields are the record of what went. Editing
                  them would only ever describe an email that does not exist. */}
              <span className="rec-cmp__sent">✓ Sent to {toName}</span>
              {links}
              <span className="rec-cmp__spacer" />
              <button type="button" className="rec-link rec-cmp__a" onClick={onClose}>
                Close
              </button>
            </div>
          ) : (
            <div className="rec-cmp__acts">
              {data.can_send ? (
                <>
                  <button
                    type="button"
                    className="rec-btn rec-btn--dark rec-cmp__send"
                    onClick={() => setConfirming(true)}
                    disabled={!body.trim() || !subject.trim()}
                  >
                    Send
                  </button>
                  {sendError && <ErrorText err={sendError} />}
                </>
              ) : (
                sendMissing
              )}
              {saveBtn}
              {links}
              <span className="rec-cmp__spacer" />
              {edited && <span className="rec-meta">Esc asks before discarding</span>}
            </div>
          )}
          {sentNotes}
        </>
      )}
      {guardEl}
    </div>
  );
}

/*
 * Over everything, outside the composer's own box: a dialog positioned inside a
 * host that animates with a transform would be placed against that host instead
 * of the screen. The wrapper carries the record's variables and type.
 */
function Layer({ children }) {
  if (typeof document === "undefined") return null;
  return createPortal(<div className="rec rec-cmp-layer">{children}</div>, document.body);
}

// "Reconnect Gmail in Settings": a new tab, so the draft in front of him survives
// the trip (the error promises "your draft is kept").
function SettingsLink() {
  return (
    <a className="rec-cmp__a" href="/settings" target="_blank" rel="noreferrer">
      Reconnect Gmail in Settings
    </a>
  );
}

/*
 * The files as the server takes them: { name, type, data } with data in base64.
 * Read only when the email is filed or sent, so a file added and taken off again
 * is never read at all.
 */
function encodeFiles(files) {
  return Promise.all(
    (files || []).map(
      (f) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const url = String(reader.result || "");
            resolve({ name: f.name, type: f.type || "", data: url.slice(url.indexOf(",") + 1) });
          };
          reader.onerror = () => reject(new Error(`${f.name} could not be read`));
          reader.readAsDataURL(f.file);
        })
    )
  );
}

/** An error beside its control, with the Settings phrase as the link it names. */
function ErrorText({ err }) {
  if (!err) return null;
  if (!err.link || !err.text.includes(err.link)) return <span className="rec-err">{err.text}</span>;
  const [before, after] = err.text.split(err.link);
  return (
    <span className="rec-err">
      {before}
      <SettingsLink />
      {after}
    </span>
  );
}
